/**
 * Admin dashboard data hook — real fetch wired to existing list endpoints.
 *
 * Strategy (no dedicated /api/admin/stats endpoint exists):
 *   - questions count  : GET /api/questions?limit=1 → total via the full
 *                        payload (limit=100 for accuracy; cursor-paged so
 *                        we take the first page's length as an under-count
 *                        but that is honest: "at least N").
 *                        IMPLEMENTATION NOTE: the questions list endpoint
 *                        does not return a total_count field. We fetch a
 *                        single page (limit=100) and report the page length.
 *                        If the corpus exceeds 100, the number will be
 *                        under-reported. Flag below marks this as NEEDS BACKEND
 *                        for a proper /api/admin/stats aggregate.
 *   - tests count      : GET /api/tests → total of first page (same caveat).
 *   - attempts count   : GET /api/admin/attempts?limit=100 → page length.
 *   - pass_rate        : No aggregate endpoint — shown as em-dash ("—").
 *                        NEEDS BACKEND: /api/admin/stats with pre-computed
 *                        pass_rate aggregate.
 *   - recent_tests     : first 5 results from GET /api/tests sorted by
 *                        created_at desc (test list returns them newest-first).
 *                        attempts per test: no per-test aggregate; shown as 0.
 *                        NEEDS BACKEND: /api/tests endpoint to include
 *                        attempt_count per test row.
 *   - recent_results   : first 5 completed attempts from
 *                        GET /api/admin/attempts?limit=5&status=completed.
 *                        Name derived from user_email (no display_name on
 *                        AdminAttemptSummary). NEEDS BACKEND: add display_name
 *                        to the attempt summary shape.
 *
 * Cross-cutting invariants:
 *   - Inv 12 (no console.log) — none.
 *   - Inv 13 (no any / @ts-ignore) — none.
 */

import { useEffect, useState } from 'react';
import { listTests } from '../../state/tests/api.ts';
import { adminApi } from '../../state/admin/api.ts';

export interface AdminDashboardStats {
  questions: number;
  tests: number;
  attempts: number;
  /** 0..1; rendered as percentage at the call site.
   *  null = no aggregate endpoint — dashboard renders "—". */
  pass_rate: number | null;
}

export interface RecentTestRow {
  id: string;
  title: string;
  questions: number;
  /** Attempt count per test — sourced from attempt_count on TestPayload. */
  attempts: number;
  status: 'draft' | 'active' | 'archived';
}

export interface RecentResultRow {
  id: string;
  /** Display name: candidate_name if set, else email prefix. */
  name: string;
  test: string;
  /** 0..100 raw score. */
  score: number;
}

export interface AdminDashboardData {
  status: 'loading' | 'ready' | 'error';
  stats: AdminDashboardStats | null;
  recent_tests: RecentTestRow[] | null;
  recent_results: RecentResultRow[] | null;
  error: string | null;
}

export function useAdminDashboardStats(): AdminDashboardData {
  const [data, setData] = useState<AdminDashboardData>({
    status: 'loading',
    stats: null,
    recent_tests: null,
    recent_results: null,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      // ── Parallel fetches — independent ──────────────────────────────────
      const [statsEnv, testsEnv, attemptsEnv] = await Promise.all([
        adminApi.getStats(),
        listTests({ limit: 100 }),
        adminApi.listAttempts({ limit: 5, status: 'completed' }),
      ]);

      if (cancelled) return;

      if (!statsEnv.ok || !testsEnv.ok || !attemptsEnv.ok) {
        const msg =
          (!statsEnv.ok && statsEnv.error.message) ||
          (!testsEnv.ok && testsEnv.error.message) ||
          (!attemptsEnv.ok && attemptsEnv.error.message) ||
          'failed to load dashboard data';
        setData({
          status: 'error',
          stats: null,
          recent_tests: null,
          recent_results: null,
          error: String(msg),
        });
        return;
      }

      const tests = testsEnv.data.items;
      const attempts = attemptsEnv.data.items;

      // ── Stats — sourced from /api/admin/stats aggregate endpoint ─────────
      const stats: AdminDashboardStats = {
        questions: statsEnv.data.questions,
        tests: statsEnv.data.tests,
        attempts: statsEnv.data.attempts,
        pass_rate: statsEnv.data.pass_rate,
      };

      // ── Recent tests — first 5, newest-first ──────────────────────────────
      const recent_tests: RecentTestRow[] = tests.slice(0, 5).map((t) => ({
        id: t.id,
        title: t.title,
        questions: t.question_count,
        attempts: t.attempt_count,
        status: t.status,
      }));

      // ── Recent results — completed attempts (fetched as status=completed) ─
      const completedAttempts = attempts.slice(0, 5);

      const recent_results: RecentResultRow[] = completedAttempts.map((a) => ({
        id: a.id,
        name: a.display_name,
        test: a.test_title,
        score: Math.round(a.score_percentage ?? 0),
      }));

      setData({
        status: 'ready',
        stats,
        recent_tests,
        recent_results,
        error: null,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return data;
}
